/**
 * ClipRelationships Component
 *
 * Flexible relationship system - any clip can relate to any other clip.
 * The meaning comes from:
 * - Tags on clips (dailies, storyboard, vfx-shot, vfx-version, etc.)
 * - Metadata stored with relationships
 * - Future VFX tracking system
 */
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  GitBranch,
  Plus,
  Trash2,
  Tag,
  Wand2,
  Unlink
} from 'lucide-react'
import { toast } from 'sonner'
import type { Clip, VfxShot, VfxShotStatus, VfxShotClip, VfxShotClipRole } from '@/types/database'
import {
  listClipRelationships,
  createClipRelationship,
  deleteClipRelationship,
  getRelationshipLabel,
  getRelationshipIcon,
  type ClipRelationshipResponse
} from '@/lib/api/clipRelationships'
import { getClipVfxShots, linkClipToVfxShot, unlinkClipFromVfxShot } from '@/lib/api/vfxShotClips'
import { LinkRelationshipDialog } from '@/components/relationships/LinkRelationshipDialog'

interface ClipRelationshipsProps {
  clip: Clip
  availableClips: Clip[]
  allVfxShots?: VfxShot[]
  allTags?: string[]
  canLink?: boolean
}

export function ClipRelationships({
  clip,
  availableClips,
  allVfxShots = [],
  allTags = [],
  canLink = true
}: ClipRelationshipsProps) {
  const [relationships, setRelationships] = useState<ClipRelationshipResponse[]>([])
  const [vfxShotLinks, setVfxShotLinks] = useState<VfxShotClip[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  // Load relationships and VFX shots on mount
  useEffect(() => {
    loadAllRelationships()
  }, [clip.id])

  const loadAllRelationships = async () => {
    setIsLoading(true)
    try {
      // Load clip relationships
      const response = await listClipRelationships(clip.id)
      setRelationships(response.relationships)

      // Load VFX shot links for this clip using the many-to-many API
      const vfxLinks = await getClipVfxShots(clip.id)
      setVfxShotLinks(vfxLinks)
    } catch (error: any) {
      console.error('Failed to load relationships:', error)
      toast.error(error.message || 'Failed to load relationships')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLinkClip = async (targetClipId: string, relationshipType: string = 'related') => {
    try {
      const newRelationship = await createClipRelationship(clip.id, {
        target_clip_id: targetClipId,
        relationship_type: relationshipType,
        metadata: {}
      })

      setRelationships([...relationships, newRelationship])
      toast.success('Clip linked successfully')
      await loadAllRelationships() // Reload to get fresh data
    } catch (error: any) {
      console.error('Failed to create relationship:', error)
      toast.error(error.message || 'Failed to link clip')
      throw error // Re-throw so dialog knows it failed
    }
  }

  const handleLinkVfxShot = async (vfxShotId: string, relationshipType: VfxShotClipRole = 'source') => {
    try {
      await linkClipToVfxShot(vfxShotId, clip.id, relationshipType)
      toast.success(`VFX shot linked as ${relationshipType}`)
      await loadAllRelationships() // Reload to get fresh data
    } catch (error: any) {
      console.error('Failed to link VFX shot:', error)
      toast.error(error.message || 'Failed to link VFX shot')
      throw error // Re-throw so dialog knows it failed
    }
  }

  const handleDeleteRelationship = async (relationshipId: string) => {
    try {
      await deleteClipRelationship(clip.id, relationshipId)
      setRelationships(relationships.filter(r => r.id !== relationshipId))
      toast.success('Relationship deleted')
    } catch (error: any) {
      console.error('Failed to delete relationship:', error)
      toast.error(error.message || 'Failed to delete relationship')
    }
  }

  const handleUnlinkVfxShot = async (linkId: string, vfxShotId: string, relationshipType: VfxShotClipRole) => {
    try {
      await unlinkClipFromVfxShot(vfxShotId, clip.id, relationshipType)
      setVfxShotLinks(vfxShotLinks.filter(link => link.id !== linkId))
      toast.success(`Unlinked VFX shot relationship`)
    } catch (error: any) {
      console.error('Failed to unlink VFX shot:', error)
      toast.error(error.message || 'Failed to unlink VFX shot')
    }
  }

  const getVfxStatusBadge = (status: VfxShotStatus) => {
    const statusConfig: Record<VfxShotStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      yellow: { label: 'Pending', variant: 'outline' },
      green: { label: 'Approved', variant: 'default' },
      in_progress: { label: 'In Progress', variant: 'secondary' },
      review_ready: { label: 'Review', variant: 'secondary' },
      approved: { label: 'Final', variant: 'default' },
      omit: { label: 'Omitted', variant: 'destructive' },
      back_in: { label: 'Back In', variant: 'outline' }
    }
    return statusConfig[status]
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Relationships</CardTitle>
          </div>
          {canLink && (
            <Button
              size="sm"
              onClick={() => setAddDialogOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Link
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Loading relationships...
          </p>
        )}

        {!isLoading && relationships.length === 0 && vfxShotLinks.length === 0 && (
          <div className="text-center py-8">
            <GitBranch className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              No relationships yet. Create links to connect related media. Tags define the meaning.
            </p>
          </div>
        )}

        {!isLoading && relationships.length > 0 && (
          <div className="space-y-3">
            {relationships.map((rel) => {
              const isSource = rel.source_clip_id === clip.id
              const relatedClip = isSource ? rel.target_clip : rel.source_clip
              const directionLabel = isSource ? 'Outgoing' : 'Incoming'

              return (
                <Card key={rel.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1 text-lg">
                        {getRelationshipIcon(rel.relationship_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-sm">
                            {relatedClip?.name || 'Unknown Clip'}
                          </h4>
                          <Badge variant="outline">
                            {getRelationshipLabel(rel.relationship_type)}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {directionLabel}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {relatedClip?.nle_unique_id && (
                            <span>NLE ID: {relatedClip.nle_unique_id}</span>
                          )}
                          {relatedClip?.tags && relatedClip.tags.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {relatedClip.tags.slice(0, 3).map((tag: string, i: number) => (
                                <Badge key={i} variant="secondary" className="text-xs px-1 py-0">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {canLink && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRelationship(rel.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* VFX Shots Section */}
        {!isLoading && vfxShotLinks.length > 0 && (
          <div className={`space-y-3 ${relationships.length > 0 ? 'mt-6 pt-6 border-t' : ''}`}>
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">VFX Shot Links ({vfxShotLinks.length})</h3>
            </div>
            {vfxShotLinks.map((link) => {
              // Find the VFX shot from allVfxShots
              const vfxShot = allVfxShots.find(shot => shot.id === link.vfx_shot_id)
              if (!vfxShot) return null

              const statusConfig = getVfxStatusBadge(vfxShot.status)
              return (
                <Card key={link.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1 text-lg">
                        <Wand2 className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-sm">{vfxShot.shot_name}</h4>
                          <Badge variant="outline" className="text-xs capitalize">
                            {link.relationship_type}
                          </Badge>
                          <Badge variant={statusConfig.variant}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Frames: {vfxShot.frame_range_start}-{vfxShot.frame_range_end}</span>
                          {vfxShot.tags && vfxShot.tags.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {vfxShot.tags.slice(0, 3).map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs px-1 py-0">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {canLink && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlinkVfxShot(link.id, link.vfx_shot_id, link.relationship_type)}
                        className="text-muted-foreground hover:text-destructive"
                        title={`Unlink ${link.relationship_type} relationship`}
                      >
                        <Unlink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* Link Relationship Dialog */}
        <LinkRelationshipDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          sourceType="clip"
          sourceId={clip.id}
          sourceName={clip.name}
          clips={availableClips}
          vfxShots={allVfxShots}
          allTags={allTags}
          onLinkClip={handleLinkClip}
          onLinkVfxShot={handleLinkVfxShot}
        />
      </CardContent>
    </Card>
  )
}
